import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../../../services/supabaseClient';
import AdminService from '../../../services/adminService';

const ClientsListPage = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [syncing, setSyncing] = useState(false);
  const [tagFilter, setTagFilter] = useState('');
  const [total, setTotal] = useState(0);
  const [sort, setSort] = useState('updated_at_desc');
  const [nationality, setNationality] = useState('');
  const [nationalities, setNationalities] = useState([]);
  const [portalModalClient, setPortalModalClient] = useState(null);
  const [portalEmail, setPortalEmail] = useState('');
  const [portalPassword, setPortalPassword] = useState('');
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError, setPortalError] = useState(null);
  const [portalResult, setPortalResult] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      // Build query with filters
      let q = supabase
        .from('clients')
        .select('*', { count: 'exact' });

      if (search.trim()) {
        const qstr = search.trim();
        // OR search across common fields
        q = q.or(
          `first_name.ilike.%${qstr}%,last_name.ilike.%${qstr}%,primary_email.ilike.%${qstr}%,primary_phone.ilike.%${qstr}%,nationality.ilike.%${qstr}%`
        );
      }

      if (nationality) {
        q = q.ilike('nationality', nationality);
      }

      // Sorting: last token is direction, rest joined are field (handles updated_at)
      const sortParts = String(sort || '').split('_');
      const dir = sortParts.pop() || 'desc';
      const field = sortParts.join('_') || 'updated_at';
      q = q.order(field, { ascending: dir === 'asc' });

      // Pagination (range is inclusive)
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;
      q = q.range(from, to);

      const { data, error, count } = await q;
      if (error) throw error;
      setRows(data || []);
      setTotal(count || 0);
    } catch (e) {
      console.error('Failed to load clients', e);
      setError('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [page, pageSize, sort, nationality]);
  useEffect(() => {
    // Fetch distinct nationalities (best-effort)
    (async () => {
      try {
        const { data, error } = await supabase
          .from('clients')
          .select('nationality')
          .not('nationality', 'is', null);
        if (error) throw error;
        const uniq = Array.from(new Set((data || []).map(r => (r.nationality || '').trim()).filter(Boolean))).sort();
        setNationalities(uniq);
      } catch (e) {
        // ignore
      }
    })();
  }, []);

  useEffect(() => {
    if (portalModalClient) {
      setPortalEmail(portalModalClient.primary_email || '');
      setPortalPassword('');
      setPortalResult(null);
      setPortalError(null);
    }
  }, [portalModalClient]);

  const closePortalModal = () => {
    setPortalModalClient(null);
    setPortalPassword('');
    setPortalResult(null);
    setPortalError(null);
  };

  const isEmailValid = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

  const handleProvisionPortal = async () => {
    if (!portalModalClient) return;

    if (!portalEmail || !isEmailValid(portalEmail)) {
      setPortalError('Please provide a valid email address.');
      return;
    }

    setPortalLoading(true);
    setPortalError(null);
    setPortalResult(null);
    try {
      const data = await AdminService.ensureClientPortalUser(portalModalClient.id, portalEmail, portalPassword || undefined);
      setPortalResult({
        message: data?.generatedPassword
          ? 'Portal account ready. Share the temporary password below with the client.'
          : 'Portal account synced successfully.',
        generatedPassword: data?.generatedPassword || null
      });
      await load();
    } catch (error) {
      console.error("Provision portal error:", error);
      setPortalError(error?.message || 'Failed to provision portal account. Check console for details.');
    } finally {
      setPortalLoading(false);
    }
  };

  const handleResetPortalPassword = async () => {
    if (!portalModalClient) return;
    setPortalLoading(true);
    setPortalError(null);
    setPortalResult(null);
    try {
      const data = await AdminService.resetClientPortalPassword(portalModalClient.id, portalPassword || undefined);
      setPortalResult({
        message: 'Password updated. Provide the temporary password to the client.',
        generatedPassword: data?.generatedPassword || portalPassword || null
      });
      await load();
    } catch (error) {
      console.error("Reset password error:", error);
      setPortalError(error?.message || 'Failed to update password. Check console for details.');
    } finally {
      setPortalLoading(false);
    }
  };

  const syncClients = async () => {
    try {
      setSyncing(true);
      const { error } = await supabase.rpc('admin_backfill_clients');
      if (error) throw error;
      await load();
    } catch (e) {
      console.error('client sync error', e);
      // Surface actionable hints for common RPC shape mismatches
      const msg = e?.message || '';
      if (/nationality/.test(msg)) {
        setError('Sync failed: submissions do not include nationality field expected by RPC. Please update the admin_backfill_clients function or ensure submissions include nationality.');
      } else {
        setError('Failed to sync clients');
      }
    } finally {
      setSyncing(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  let paged = rows;
  if (tagFilter.trim()) {
    const tq = tagFilter.trim().toLowerCase();
    paged = (rows || []).filter((c) => Array.isArray(c.tags) && c.tags.some((t) => String(t).toLowerCase().includes(tq)));
  }

  const exportCsv = () => {
    const headers = ['id', 'first_name', 'last_name', 'primary_email', 'primary_phone', 'nationality', 'dob', 'updated_at'];
    const lines = [headers.join(',')];
    (rows || []).forEach(c => {
      const vals = headers.map(h => {
        const v = c[h] == null ? '' : String(c[h]);
        return '"' + v.replaceAll('"', '""') + '"';
      });
      lines.push(vals.join(','));
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
    a.download = `clients-page-${page}-${ts}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return (
    <div className="min-h-screen bg-gray-900 font-mono text-white flex flex-col items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4" />
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-900 font-mono text-white p-6">
      {/* Header */}
      <div className="mb-6 flex items-end justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">👥 Client Management</h2>
          <p className="text-gray-400">Manage and track all client information, submissions, and cases</p>
        </div>
        <button
          onClick={() => navigate('/admin/clients/new')}
          className="px-4 py-2 rounded text-sm font-bold bg-blue-600 text-white hover:bg-blue-700 uppercase border border-blue-500 shadow-lg shadow-blue-900/20"
        >
          + Add New Client
        </button>
      </div>

      {/* Controls */}
      <div className="bg-gray-800 border border-gray-700 rounded-lg shadow">
        <div className="p-6">
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={syncClients}
              disabled={syncing}
              className="px-3 py-1.5 rounded text-sm font-medium bg-gray-700 text-white border border-gray-600 hover:bg-gray-600"
            >
              {syncing ? 'Syncing…' : '🔄 Sync from submissions'}
            </button>
            <button onClick={exportCsv} className="px-3 py-1.5 rounded text-sm font-medium bg-gray-700 text-white border border-gray-600 hover:bg-gray-600">📊 Export CSV</button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <input
              className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="🔍 Search name/email/phone/nationality"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              onKeyDown={(e) => { if (e.key === 'Enter') { setPage(1); load(); } }}
            />
            <input
              className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="🏷️ Filter by tag"
              value={tagFilter}
              onChange={(e) => { setTagFilter(e.target.value); }}
            />
            <select
              value={nationality}
              onChange={(e) => { setNationality(e.target.value); setPage(1); }}
              className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All nationalities</option>
              {nationalities.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
            <select
              value={sort}
              onChange={(e) => { setSort(e.target.value); setPage(1); }}
              className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="updated_at_desc">Updated (newest)</option>
              <option value="updated_at_asc">Updated (oldest)</option>
              <option value="created_at_desc">Created (newest)</option>
              <option value="created_at_asc">Created (oldest)</option>
              <option value="last_name_asc">Last name (A→Z)</option>
              <option value="last_name_desc">Last name (Z→A)</option>
              <option value="first_name_asc">First name (A→Z)</option>
              <option value="first_name_desc">First name (Z→A)</option>
            </select>
            <select
              value={pageSize}
              onChange={(e) => { setPageSize(parseInt(e.target.value) || 20); setPage(1); }}
              className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {[10, 20, 50, 100].map(n => <option key={n} value={n}>{n}/page</option>)}
            </select>
            <button onClick={() => load()} className="px-3 py-1.5 rounded text-sm font-medium bg-blue-600 text-white hover:bg-blue-700">Apply</button>
          </div>
        </div>
      </div>

      {error && (
        <div className="p-4 rounded border mb-6 bg-red-900/20 border-red-800 text-red-400">
          <p>{error}</p>
        </div>
      )}

      {/* Table */}
      <div className="bg-gray-800 border border-gray-700 rounded-lg shadow">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead className="bg-gray-900/50 text-gray-400 uppercase text-xs">
              <tr>
                <th className="p-4">Name</th>
                <th className="p-4">Email</th>
                <th className="p-4">Phone</th>
                <th className="p-4">Nationality</th>
                <th className="p-4">Tags</th>
                <th className="p-4">Updated</th>
                <th className="p-4" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {paged.map(c => (
                <tr key={c.id} className="hover:bg-gray-700/50 transition-colors">
                  <td className="p-4 font-semibold text-white">{`${c.first_name || ''} ${c.last_name || ''}`.trim() || c.primary_email || c.id}</td>
                  <td className="p-4 text-gray-300">{c.primary_email || '-'}</td>
                  <td className="p-4 text-gray-300">{c.primary_phone || '-'}</td>
                  <td className="p-4 text-gray-300">{c.nationality || '-'}</td>
                  <td className="p-4">
                    <div className="flex flex-wrap gap-1">
                      {(Array.isArray(c.tags) ? c.tags : []).slice(0, 4).map((t, i) => (
                        <span key={i} className="inline-flex items-center px-2 py-1 text-xs bg-blue-900/30 text-blue-400 border border-blue-900/50 rounded-full">{t}</span>
                      ))}
                      {(Array.isArray(c.tags) ? c.tags : []).length > 4 && (
                        <span className="text-gray-400 text-xs">+{(c.tags.length - 4)}</span>
                      )}
                    </div>
                  </td>
                  <td className="p-4 text-gray-400">{c.updated_at ? new Date(c.updated_at).toLocaleString() : '-'}</td>
                  <td className="p-4 text-right">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Link to={`/admin/clients/${c.id}`} className="px-3 py-1.5 rounded text-sm font-medium bg-blue-600 text-white hover:bg-blue-700">View</Link>
                      <button onClick={() => navigate(`/admin/cases/new?clientId=${c.id}`)} className="px-3 py-1.5 rounded text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700">New Case</button>
                      <button onClick={() => navigate(`/admin/agreements/builder?clientId=${c.id}`)} className="px-3 py-1.5 rounded text-sm font-medium bg-gray-700 text-white border border-gray-600 hover:bg-gray-600">Agreement</button>
                      <button onClick={() => setPortalModalClient(c)} className="px-3 py-1.5 rounded text-sm font-medium bg-gray-700 text-white border border-gray-600 hover:bg-gray-600">
                        {c.portal_user_id ? 'Portal' : 'Provision Portal'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {paged.length === 0 && (
                <tr><td colSpan={7} className="p-4 text-center py-12 text-gray-400">No clients found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between mt-6 text-sm text-gray-400">
        <div>Showing {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, total)} of {total}</div>
        <div className="flex items-center gap-2">
          <button disabled={page <= 1} onClick={() => setPage(1)} className="btn-sm disabled:opacity-50">First</button>
          <button disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))} className="btn-sm disabled:opacity-50">Prev</button>
          <span className="font-medium">Page {page} / {totalPages}</span>
          <button disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))} className="btn-sm disabled:opacity-50">Next</button>
          <button disabled={page >= totalPages} onClick={() => setPage(totalPages)} className="btn-sm disabled:opacity-50">Last</button>
        </div>
      </div>

      {/* Portal Modal */}
      {portalModalClient && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-800 border border-gray-700 rounded-lg p-6 max-w-lg w-full mx-4 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-white">Client Portal Access</h3>
                <p className="text-sm text-gray-500">{portalModalClient.first_name || ''} {portalModalClient.last_name || ''}</p>
              </div>
              <button onClick={closePortalModal} className="text-gray-400 hover:text-gray-600 p-2">×</button>
            </div>

            <div className="space-y-3">
              <label className="text-sm font-medium text-gray-300">
                Portal Email <span className="text-red-400">*</span>
                <input
                  className="admin-input mt-1"
                  value={portalEmail}
                  onChange={(e) => setPortalEmail(e.target.value)}
                  placeholder="client@email.com"
                />
              </label>
              <label className="text-sm font-medium text-gray-300">
                Temporary Password (optional)
                <input
                  className="admin-input mt-1"
                  value={portalPassword}
                  onChange={(e) => setPortalPassword(e.target.value)}
                  placeholder="Leave blank to auto-generate"
                  type="text"
                />
              </label>
              <p className="text-xs text-gray-500">
                {portalModalClient.portal_user_id ? 'Update the password or re-sync the account. Note: Changing the email here will update the client record.' : 'Provisioning will create a Supabase Auth user and link it to this client.'}
              </p>
            </div>

            {portalError && (
              <div className="p-4 rounded border mb-6 bg-red-900/20 border-red-800 text-red-400 text-sm">
                <strong>Error:</strong> {portalError}
              </div>
            )}

            {portalResult && (
              <div className="p-4 rounded border mb-6 bg-green-900/20 border-green-800 text-green-400">
                <p>{portalResult.message}</p>
                {portalResult.generatedPassword && (
                  <p className="font-mono text-base bg-black/20 p-2 rounded mt-2">{portalResult.generatedPassword}</p>
                )}
              </div>
            )}

            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={handleProvisionPortal}
                disabled={portalLoading}
                className="px-3 py-1.5 rounded text-sm font-medium bg-gray-700 text-white border border-gray-600 hover:bg-gray-600 disabled:opacity-50"
              >
                {portalLoading ? 'Working…' : portalModalClient.portal_user_id ? 'Update / Re-Sync' : 'Provision Portal'}
              </button>
              <button
                onClick={handleResetPortalPassword}
                disabled={portalLoading || !portalModalClient.portal_user_id}
                className="px-3 py-1.5 rounded text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:bg-blue-800"
              >
                {portalLoading ? 'Updating…' : 'Set / Reset Password'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientsListPage;
